/**
 * One generation job as a row: thumbnail, view, status, sub-line, notes, error and its actions.
 * Shared by the Generate index and the setup form's results column.
 */
import type { GenerationJobView } from "@genz/platform";
import { Icon } from "../ui/Icon.js";
import { RowAction } from "../ui/RowAction.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { WorkingTimer } from "../ui/WorkingTimer.js";
import type { DraftJob } from "../stores/generateDraft.js";
import { inPassTwo, isOngoing, isReady, jobBadge, jobLabel } from "./candidates.js";
import { ErrorNotice } from "../ui/ErrorNotice.js";
import { useGreyThumb } from "./useGreyThumb.js";

const HANDOFF_STEPS = ["Generated", "In template", "Published"] as const;

function handoffIndex(job: GenerationJobView): number {
  return job.handoffStep === "published" ? 2 : job.handoffStep === "in_template" ? 1 : 0;
}

/** Generated → In template → Published, with the step this job reached current. */
function HandoffSteps({ job, testId }: { job: GenerationJobView; testId: string }) {
  const at = handoffIndex(job);
  return (
    <ol className="generate-panel__job-steps caption" aria-label="Progress" data-testid={testId}>
      {HANDOFF_STEPS.map((label, k) => (
        <li key={label} className={k <= at ? "is-done" : undefined} aria-current={k === at ? "step" : undefined}>
          {label}
        </li>
      ))}
    </ol>
  );
}

/** When this session first saw each job: the clock's start for a job sent without `createdAt`. */
const firstSeen = new Map<string, number>();

function seenAt(id: string): number {
  if (!firstSeen.has(id)) firstSeen.set(id, Date.now());
  return firstSeen.get(id)!;
}

/** How long a job has run, or took. A finished job without both ends shows nothing: its age is
 *  not its duration. */
function JobTime({ job, failed, testId }: { job: GenerationJobView; failed: boolean; testId: string }) {
  if (jobBadge(job).inFlight) {
    return (
      <span className="generate-panel__job-time" data-testid={testId}>
        <WorkingTimer startedAt={job.createdAt ?? seenAt(job.id)} />
      </span>
    );
  }
  if (!job.createdAt || !job.finishedAt) return null;
  return (
    <span className="generate-panel__job-time" data-testid={testId}>
      <WorkingTimer startedAt={job.createdAt} endedAt={job.finishedAt}
        state={failed ? "failed" : "done"} label={failed ? undefined : "Took"} />
    </span>
  );
}

/** A thumbnail with no `open` is a picture only; `guide` marks the magenta result, drawn grey. */
interface Thumb {
  url: string; open?: () => void; action: string; title: string; alt: string; testId: string; guide?: boolean;
}

/** Never the magenta image (owner, 2026-10-03): the clear image when the job has one, else the
 *  result with its print guide painted grey. */
function thumbOf(j: GenerationJobView, admin: boolean, onPreview: () => void): Thumb | null {
  // A4: the larger preview is the magenta image, which a customer never opens.
  const preview = isReady(j) && admin
    ? { open: onPreview, action: "Preview generated view", title: "Open a larger preview" }
    : undefined;
  if (j.clearUrl && (isReady(j) || inPassTwo(j))) {
    return { action: "Clear image", title: "Clear image", ...preview, url: j.clearUrl, alt: "Clear image",
      testId: "job-clear" };
  }
  if (preview && j.resultUrl) return { ...preview, url: j.resultUrl, alt: "Generated view", testId: "job-result", guide: true };
  return null;
}

export interface JobRowProps {
  entry: DraftJob;
  i: number;
  admin: boolean;
  active: boolean;
  onPreview: () => void;
  onOpenTemplate?: () => void;
  /** Regenerate / Reject for a job with check notes; only while its inputs are still here. */
  retake?: { question: string; regenerate: () => void; reject: () => void };
  showSteps?: boolean;
  /** The thumbnail's link did not load (signed links expire), or did. */
  onImageError?: () => void;
  onImageLoad?: () => void;
  /** Stops an ongoing job; offered on those rows only. */
  onCancel?: () => void;
  /** Runs the magenta step again; offered where the cloud says it can. */
  onRetryMagenta?: () => void;
  /** A request for this row is out. */
  busy?: boolean;
  /** False where the image is already shown large (the run gallery). */
  thumb?: boolean;
}

export function JobRow({
  entry, i, admin, active, onPreview, onOpenTemplate, retake, showSteps, onImageError, onImageLoad,
  onCancel, onRetryMagenta, busy, thumb: withThumb = true,
}: JobRowProps) {
  const j = entry.job;
  const ready = isReady(j);
  const failed = j.status === "failed" || j.status === "held";
  const b = jobBadge(j);
  const thumb = withThumb ? thumbOf(j, admin, onPreview) : null;
  const grey = useGreyThumb(`${j.id}:${j.finishedAt ?? ""}`, thumb?.guide ? thumb.url : null, onImageError);
  const src = thumb?.guide ? grey : thumb?.url;
  return (
    <li
      className={`generate-panel__job${active ? " is-active" : ""}`}
      data-testid={`job-status-${i}`}
      data-job={entry.id}
      data-status={j.status}
    >
      {thumb && src && !thumb.open ? (
        <img className="generate-panel__job-img" src={src} alt={`${thumb.alt}: ${entry.label}`}
          onError={onImageError} onLoad={onImageLoad} data-testid={`${thumb.testId}-${i}`} />
      ) : thumb && src ? (
        <button
          type="button"
          className="generate-panel__job-thumb-btn"
          onClick={thumb.open}
          title={thumb.title}
          aria-label={`${thumb.action}: ${entry.label}`}
          data-testid={`job-thumb-btn-${i}`}
        >
          <img className="generate-panel__job-img" src={src} alt={`${thumb.alt}: ${entry.label}`}
            onError={onImageError} onLoad={onImageLoad} data-testid={`${thumb.testId}-${i}`} />
        </button>
      ) : withThumb && (
        <div
          className={`generate-panel__job-thumb-placeholder${failed ? " is-failed" : ""}`}
          aria-hidden="true"
          data-testid={`job-thumb-placeholder-${i}`}
        >
          {ready && <Icon name="check" size={16} />}
        </div>
      )}
      <span className="generate-panel__job-label">{entry.label}</span>
      <span className={`badge badge--plain badge--${b.variant} generate-panel__job-badge${b.inFlight ? " generate-panel__job-state--live" : ""}`}>
        {jobLabel(j)}
      </span>
      <JobTime job={j} failed={failed} testId={`job-time-${i}`} />
      {ready && j.publishedTemplateId && onOpenTemplate && (
        <RowAction label={`Open template made from ${entry.label}`} hint="Open template" icon="templates"
          onClick={onOpenTemplate} data-testid={`job-open-template-${i}`} />
      )}
      {ready && j.resultUrl && admin && (
        <RowAction label={`Preview generated view: ${entry.label}`} hint="Open a larger preview" icon="expand"
          className="generate-panel__job-preview" onClick={onPreview} data-testid={`job-preview-btn-${i}`} />
      )}
      {isOngoing(j) && onCancel && (
        <ConfirmPopover message="Cancel this generation? An image already being made can still be charged."
          confirmLabel="Cancel generation" cancelLabel="Keep" danger onConfirm={onCancel}>
          {(toggle) => (
            <RowAction label={`Cancel ${entry.label}`} hint="Cancel this generation" icon="close" tone="quiet"
              onClick={toggle} disabled={busy} busy={busy} data-testid={`job-cancel-${i}`} />
          )}
        </ConfirmPopover>
      )}
      {j.canRetryMagenta && onRetryMagenta && (
        <ConfirmPopover message="Run the print guide step again? It uses the settings saved now and costs credits. The first image is kept."
          confirmLabel="Retry print guide" onConfirm={onRetryMagenta}>
          {(toggle) => (
            <RowAction label={`Retry print guide for ${entry.label}`} hint="Run the print guide step again" icon="brush" tone="quiet"
              onClick={toggle} disabled={busy} busy={busy} data-testid={`job-retry-magenta-${i}`} />
          )}
        </ConfirmPopover>
      )}
      {entry.details && (
        <small className="faint generate-panel__job-sub" data-testid={`job-sub-${i}`}>{entry.details}</small>
      )}
      {ready && showSteps && <HandoffSteps job={j} testId={`job-step-${i}`} />}
      {ready && (j.checkNotes?.length ?? 0) > 0 && (
        <small className="generate-panel__job-note" role="status" data-testid={`job-note-${i}`}>
          {j.checkNotes!.map((n, index) => <ErrorNotice key={index} detail={n} reference={j.id} advisory />)}
        </small>
      )}
      {ready && (j.checkNotes?.length ?? 0) > 0 && retake && (
        <>
          <ConfirmPopover message={retake.question} confirmLabel="Regenerate" onConfirm={retake.regenerate}>
            {(toggle) => (
              <RowAction label={`Regenerate ${entry.label}`} icon="refresh" onClick={toggle}
                data-testid={`job-regenerate-${i}`} />
            )}
          </ConfirmPopover>
          <RowAction label={`Reject ${entry.label}`} icon="close" onClick={retake.reject}
            data-testid={`job-reject-${i}`} />
        </>
      )}
      {failed && (
        <span className="banner banner--danger generate-panel__job-error" data-testid={`job-error-${i}`}>
          <ErrorNotice detail={j.error ?? j.status} reference={j.id} />
        </span>
      )}
    </li>
  );
}
